import { Form } from './form';
import { SubscriptionView } from '../view/subscription-view';
import { Config } from '../config';
import { FormTypes } from './form-types';
import { QuantityForm } from './quantity-form';
import { Order } from '../config/order';
import { Product } from '../config/product';
import { ProductType } from '../config/product-type';
import { QuantityValidator } from '../validators/quantity-validator';

export class SubscriptionForm extends Form {

    private subscriptions: Product[] = [];
    type = FormTypes.Subscription;
    view: SubscriptionView = new SubscriptionView();
    private selectedPlan = '';
    currentSubscription: any = {};
    quantityForm: QuantityForm = {} as QuantityForm;
    quantity = 1;
    // hideCreditCard = false; // flag is up when selected product is no_payment, use it in widget to deactivate payment form

    constructor(step: number | null = null) {
        super();
        this.step = step;
    }

    async init(config: Config, callback: any) {
        return super.init(config, callback).then(res => {
            this.subscriptions = this.config.products.filter((product: Product) => {
                return (
                    product.type === ProductType.SUBSCRIPTION
                    || (this.includeNoPayment() && product.type === ProductType.NO_PAYMENT))
                    && !product.prices
                    && !product.addon;
            });
    
            if (this.subscriptions.every((subscription) => subscription.showQuantity)) {
                this.quantityForm = new QuantityForm();
                return this.quantityForm.init({}, {
                    setQuantity: this.setQuantity.bind(this)
                }).then(response => {
                    this.validators.push(new QuantityValidator());
                });
            }
            return Promise.resolve();
        });
    }

    setQuantity() {
        if (this.quantityForm.quantity !== 0) {
            this.quantity = this.quantityForm.quantity;
        } else {
            this.quantity = 1;
        }
        this.paymentData.amount = this.quantity * this.currentSubscription.amount;
        if (this.config.products.length > 1) {
            this.paymentData.message = `Subscription: ${this.formatMoney(`${this.paymentData.amount}`, this.config.settings.currency)}`;
        }
        this.triggerEvent('amountChange');
    }

    async render() {
        this.view.setConfig(this.config);
        if (this.subscriptions.length > 1) {
            this.view.renderMultipleProducts(this.subscriptions, this.onSelectedPlanChange.bind(this));
        }
        this.currentSubscription = this.subscriptions[0] || {};
        this.paymentData.amount = this.currentSubscription.amount || 0;
        this.selectedPlan = this.currentSubscription.subscriptionPlanId || '';
        if (this.config.products.length > 1) {
            this.paymentData.message = `Subscription: ${this.formatMoney(`${this.paymentData.amount}`, this.config.settings.currency)}`;
        }
        if (this.subscriptions.every((subscription) => subscription.showQuantity)) {
            const quantityElementId = this.currentSubscription.type;
            this.validationContext = { id: quantityElementId };
            await this.quantityForm.render(quantityElementId, this.currentSubscription.name);
        }

        if (this.step) {
            this.hideForm();
        }

        this.view.preselectDfProductId();

        return Promise.resolve();
    }

    onSelectedPlanChange(selectedProduct: any) {
        this.currentSubscription = this.subscriptions.find((subscription: Product) => subscription.id === (selectedProduct.planId || ''));

        let amount = 0;

        if (!this.currentSubscription) {
            this.isNoPayment = true;

            this.currentSubscription = {type: ProductType.NO_PAYMENT, name: 'No payment', amount: 0, showQuantity: true, addon: false, noPayment: true, currency: this.config.settings.currency};

            amount = 0;
        } else {
            this.isNoPayment = false;
            amount = this.currentSubscription.amount * this.quantity;
            this.selectedPlan = selectedProduct.planId || '';
        }


        if (this.quantityForm.setProductName) {
            this.quantityForm.setProductName(this.currentSubscription.name);
        }
        this.paymentData.amount = +amount;
        this.paymentData.message = `Subscription: ${this.formatMoney(`${this.paymentData.amount}`, this.config.settings.currency)}`;
        this.triggerEvent('resetDiscount');
        this.triggerEvent('amountChange');
    }

    submit(order: Order) {
        if (this.currentSubscription.showQuantity) {
            this.currentSubscription.quantity = this.quantity;
        }

        let selectedProducts = this.getSelectedProducts() || [this.currentSubscription];
        selectedProducts = selectedProducts.map((product: any) => {
            if (product.type === ProductType.SUBSCRIPTION) {
                product.subscriptionPlanId = product.subscriptionPlanId || product.id;
                delete product.id;
            }
            return product;
        });

        selectedProducts = this.setDiscount(selectedProducts);

        selectedProducts.forEach(product => {
            order.selectedProducts.push(product);
        });

        return Promise.resolve(order);
    }

    getSelectedProducts() {
        const viewData = this.view.getSelectedProduct();
        if (viewData) {
            const dfProducts = [viewData];
            const showQuantity = this.currentSubscription.showQuantity;
            const quantity = this.quantity;
            return dfProducts.map((item: any) => {
                if (!item) {
                    return;
                }
                const selectedSubscr: any = {
                    dfProductId: item.getAttribute('df_product_id'),
                    type: ProductType.SUBSCRIPTION,
                    subscriptionPlanId: item.getAttribute('value'),
                    name: item.getAttribute('product_name'),
                    amount: item.getAttribute('data-amount'),
                    interval: this.getInterval(item),
                    maxCharges: this.getMaxCharges(item)
                };
                if (showQuantity) {
                    selectedSubscr.quantity = quantity;
                }
                return selectedSubscr;
            });
        }
        return [this.currentSubscription];
    }

    private getInterval = (item: any) => {
        const interval = item.getAttribute('interval');
        return interval && interval !== null && interval !== undefined && interval !== 'undefined' ? interval : null;
    }

    private getMaxCharges = (item: any) => {
        const maxCharges = item.getAttribute('max-charges');
        return maxCharges && maxCharges !== null && maxCharges !== undefined && maxCharges !== 'undefined' ? maxCharges : null;
    }

    private setDiscount(selectedProducts: Array<any>) {
        if (this.config.settings && this.config.settings.couponId) {
            const coupon = this.config.settings.couponId;
            return selectedProducts.map((item: any) => {
                item.couponId = coupon.id;
                return item;
            });
        }
        return selectedProducts;
    }

    private includeNoPayment() {
        return this.config.products.every((product: Product) => product.type === ProductType.SUBSCRIPTION);
    }

    showForm() {
        this.view.showForm();
        if (this.subscriptions.every((subscriptions) => subscriptions.showQuantity)) {
            this.quantityForm.showForm();
        }
    }

    hideForm() {
        this.view.hideForm();
        if (this.subscriptions.every((subscriptions) => subscriptions.showQuantity)) {
            this.quantityForm.hideForm();
        }
    }

}
